import type { Service } from "@App/types";
import type { ServiceGroup, ServiceGroupDefinition } from "@App/utils/groupServices";

/**
 * The category grouping for DHL's service selection.
 *
 * DHL's tire services are near-identical from a driver's point of view, so they collapse behind a
 * single category tile and are only told apart in the step after it. Everything else — currently
 * only UVV (156) — stays a tile of its own.
 */
export const dhlServiceCategories: ServiceGroupDefinition[] = [
  {
    label: "Reifenservice",
    serviceIds: [
      100, // Räderwechsel
      235, // Montage Winterkomplettradsatz
      232, // Radeinlagerung
      234, // Kalibrierung RDKS
      // Radauslagerung does not exist as a service yet; its id follows once it is created.
    ],
  },
];

/**
 * The service selection split into category tiles and ungrouped services.
 */
export type DhlServiceSelection = {
  categories: ServiceGroup[];
  services: Service[];
};

/**
 * A category is only worth the extra step once it holds more than one service.
 */
const MINIMUM_CATEGORY_SIZE = 2;

/**
 * Resolves the category definitions against the services DHL is actually assigned.
 *
 * Unlike `groupServices`, which files every leftover under a labelled rest group, the services no
 * category claims stay ungrouped here — they are rendered as ordinary tiles beside the categories.
 *
 * A category that ends up with a single service is dissolved back into that flat list: a tile that
 * leads to a one-option step is pure friction, and it keeps the definition above safe against ids
 * DHL turns out not to be assigned.
 */
export const splitIntoCategories = (
  services: Service[],
  definitions: ServiceGroupDefinition[],
): DhlServiceSelection => {
  const claimed = new Set<Service>();
  const categories: ServiceGroup[] = [];

  for (const { label, serviceIds } of definitions) {
    // Ids are compared as strings: GraphQL hands back `ID` as a string, the definitions list numbers.
    const claimable = serviceIds
      .map((id) => services.find((service) => String(service.id) === String(id)))
      .filter((service): service is Service => service !== undefined && !claimed.has(service));

    if (claimable.length < MINIMUM_CATEGORY_SIZE) continue;

    for (const service of claimable) {
      claimed.add(service);
    }
    categories.push({ label, services: claimable });
  }

  return {
    categories,
    // Keeps the `pivot.sort_order` order `useServices` already applied.
    services: services.filter((service) => !claimed.has(service)),
  };
};
